import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { AlertCircle, Clock } from "lucide-react";
import { format } from "date-fns-tz";
import CalendarService from "../../../services/calendar/CalendarService";
import { CountryPhoneSelector } from "../../../components/CountryPhoneSelector";
import { normalizePhone } from "../../../services/countries/countryData";
import { confirmDialog, showError, showSuccess } from "../../../components/alertas";
import Sheet from "../../../components/sheet/Sheet";
import useMediaQuery from "../../../hooks/useMediaQuery";

const TIME_ZONE = "America/Mexico_City";

const formatDateLabel = (dateStr) => {
    const date = new Date(dateStr + "T12:00:00");
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    const dayName = date.toLocaleDateString('es-MX', { weekday: 'short' });
    const sub = `${date.getDate()} ${date.toLocaleDateString('es-MX', { month: 'short' })}`;
    if (isToday) return { label: "Hoy", sub };
    if (isTomorrow) return { label: "Mañana", sub };
    return { label: dayName.charAt(0).toUpperCase() + dayName.slice(1).replace('.', ''), sub };
};

const convertTo24Hour = (time12h) => {
    if (time12h.includes(':') && !time12h.includes('AM') && !time12h.includes('PM')) return time12h;
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') hours = modifier === 'AM' ? '00' : '12';
    else if (modifier === 'PM') hours = String(parseInt(hours, 10) + 12);
    return `${hours.padStart(2, '0')}:${minutes}`;
};

/**
 * Alta de cita desde el panel (teléfono / mostrador): servicio → profesional → día →
 * hora → datos del cliente. Bottom sheet en móvil, panel lateral en escritorio.
 * El estado se reinicia al cerrar.
 */
export default function AppointmentSheet({ open, onClose, clientData, onSuccess }) {
    const isDesktop = useMediaQuery("(min-width: 992px)");
    const staff = clientData?.staff || [];
    const singleStaffId = staff.length === 1 ? staff[0].id : "";

    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedStaffId, setSelectedStaffId] = useState(singleStaffId);
    const [availableDays, setAvailableDays] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedTime, setSelectedTime] = useState(null);
    const [loadingDays, setLoadingDays] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [nombre, setNombre] = useState("");
    const [selectedCountry, setSelectedCountry] = useState("MX");
    const [telefono, setTelefono] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [saving, setSaving] = useState(false);
    const timeSlotsRef = useRef(null);
    const nameRef = useRef(null);

    // Al elegir la hora aparece el campo del nombre y se enfoca. flushSync: el campo tiene
    // que existir y enfocarse DENTRO del toque; si se espera al render, iOS Safari no abre
    // el teclado. Si ya se escribió el nombre (cambió de hora), no se le quita el foco.
    const chooseTime = (start) => {
        flushSync(() => setSelectedTime(start));
        const input = nameRef.current;
        if (!input || nombre.trim()) return;
        input.focus({ preventScroll: true });
        // En celular lo centra useSheetViewport cuando abre el teclado; en escritorio no
        // hay teclado, así que aquí se trae a la vista dentro del sheet.
        if (window.matchMedia?.("(pointer: fine)").matches) input.scrollIntoView({ block: "center", behavior: "smooth" });
    };

    useEffect(() => {
        if (!open || !clientData?.business?.services) { setServices([]); return; }
        setServices(clientData.business.services.map((service, index) => ({ ...service, id: service.id || `service-${index}` })));
    }, [open, clientData]);

    // Días disponibles cuando hay servicio y profesional
    useEffect(() => {
        if (!selectedService) { setAvailableDays([]); setSelectedDate(null); return; }
        if (staff.length > 1 && !selectedStaffId) { setAvailableDays([]); return; }
        let cancelled = false;
        (async () => {
            setLoadingDays(true);
            try {
                const response = await CalendarService.getAvailableDays(clientData.id, selectedStaffId);
                if (cancelled) return;
                const days = response.data.map(d => ({ ...d, dateStr: d.date.split('T')[0] }));
                setAvailableDays(days);
                if (days.length > 0) setSelectedDate(days[0].dateStr);
            } catch (error) {
                if (!cancelled) showError("Error al cargar días disponibles");
            } finally {
                if (!cancelled) setLoadingDays(false);
            }
        })();
        return () => { cancelled = true; };
        // staff.length deriva de clientData
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedService, clientData, selectedStaffId]);

    // Horas del día elegido
    useEffect(() => {
        if (!selectedDate || !selectedService) return;
        let cancelled = false;
        (async () => {
            setLoadingSlots(true);
            setSelectedTime(null);
            try {
                const response = await CalendarService.getAvailableSlots(clientData.id, selectedDate, selectedService.durationMinutes, selectedStaffId);
                if (cancelled) return;
                const now = new Date();
                let slots = response.data;
                if (new Date(selectedDate + "T12:00:00").toDateString() === now.toDateString()) {
                    const currentTime = format(now, "HH:mm", { timeZone: TIME_ZONE });
                    slots = slots.filter(slot => slot.start > currentTime);
                }
                setAvailableSlots(slots);
                setTimeout(() => timeSlotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
            } catch (error) {
                if (!cancelled) showError("Error al cargar horarios");
            } finally {
                if (!cancelled) setLoadingSlots(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedDate, selectedService, clientData, selectedStaffId]);

    const reset = () => {
        setSelectedService(null);
        setSelectedStaffId(singleStaffId);
        setAvailableDays([]); setSelectedDate(null); setAvailableSlots([]); setSelectedTime(null);
        setNombre(""); setSelectedCountry("MX"); setTelefono(""); setPhoneError("");
    };
    const handleClose = () => { reset(); onClose(); };

    // skipWhatsappVerification: el admin ya fue advertido de que el número no tiene WhatsApp
    // y aun así quiere agendar. El backend omite la verificación cuando llega en true.
    const submitAppointment = async (skipWhatsappVerification = false) => {
        setSaving(true);
        let askWhatsapp = false;
        try {
            const time24h = convertTo24Hour(selectedTime);
            const start = new Date(selectedDate + "T" + time24h + ":00");
            await CalendarService.createAppointmentAdmin({
                clientId: clientData.id,
                staffId: selectedStaffId || singleStaffId,
                nombre,
                celular: telefono ? normalizePhone(telefono, selectedCountry) : "",
                country: selectedCountry,
                servicio: selectedService.name,
                inicio_iso: start.toISOString(),
                fin_iso: new Date(start.getTime() + selectedService.durationMinutes * 60000).toISOString(),
                skipWhatsappVerification,
            });
            showSuccess("Cita agendada exitosamente");
            onSuccess?.();
            handleClose();
        } catch (error) {
            // El número no tiene WhatsApp: en vez de fallar, advertimos y dejamos que el admin decida.
            if (error.response?.data?.error === 'WHATSAPP_NUMBER_NOT_FOUND') askWhatsapp = true;
            else showError("Error al agendar la cita");
        } finally {
            setSaving(false);
        }
        if (askWhatsapp) {
            const ok = await confirmDialog({
                title: "El número no tiene WhatsApp",
                text: "Este cliente no recibirá confirmación, recordatorios ni podrá modificar su cita por WhatsApp. ¿Quieres agendar de todos modos?",
                confirmButtonText: "Agendar igual",
                cancelButtonText: "Cancelar",
                icon: "warning",
            });
            if (ok) submitAppointment(true);
        }
    };

    const canSubmit = !!selectedTime && !!nombre.trim() && !phoneError && !saving;
    const selectedDateFormatted = selectedDate ? new Date(selectedDate + "T12:00:00").toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' }) : "";
    const staffReady = staff.length <= 1 || !!selectedStaffId;

    return (
        <Sheet open={open} onClose={handleClose} side={isDesktop ? "right" : "bottom"} title="Nueva cita" subtitle="Para citas por teléfono o en mostrador" className="cp-vars" autoFocus={false}
            footer={
                <div className="cp-sheet-actions">
                    <button type="button" className="cp-btn" onClick={handleClose} disabled={saving}>Cancelar</button>
                    <button type="button" className="cp-btn cp-btn-primary" onClick={() => submitAppointment(false)} disabled={!canSubmit}>
                        {saving ? <><span className="cp-spin" /> Agendando…</> : "Agendar cita"}
                    </button>
                </div>
            }
        >
            <div className="cp-newappt">
                <label className="cp-field">
                    <span>Servicio</span>
                    <select className="cp-input" value={selectedService?.id || ""} onChange={(e) => { setSelectedService(services.find(s => s.id === e.target.value) || null); setSelectedDate(null); setSelectedTime(null); }}>
                        <option value="">Elige un servicio</option>
                        {services.map(service => (
                            <option key={service.id} value={service.id}>{service.name} · {service.durationMinutes} min · ${service.price}</option>
                        ))}
                    </select>
                </label>

                {staff.length > 1 && (
                    <div className="cp-field">
                        <span>Profesional</span>
                        <div className="cp-segment-scroll">
                            <div className="cp-segment" role="tablist">
                                {staff.map(s => (
                                    <button key={s.id} type="button" role="tab" aria-selected={selectedStaffId === s.id} className={`cp-segment-btn ${selectedStaffId === s.id ? "is-active" : ""}`} onClick={() => { setSelectedStaffId(s.id); setSelectedDate(null); setSelectedTime(null); }}>{s.name}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {selectedService && staffReady && (
                    <div className="cp-field">
                        <span>Fecha</span>
                        {loadingDays ? (
                            <div className="cp-newappt-loading"><span className="cp-spin" /> Buscando días con lugar…</div>
                        ) : availableDays.length === 0 ? (
                            <div className="cp-note"><AlertCircle size={16} /> No hay días disponibles.</div>
                        ) : (
                            <div className="cp-choice-strip">
                                {availableDays.map((day) => {
                                    const { label, sub } = formatDateLabel(day.dateStr);
                                    return (
                                        <button key={day.dateStr} type="button" className={`cp-choice ${selectedDate === day.dateStr ? "is-active" : ""}`} onClick={() => setSelectedDate(day.dateStr)}>
                                            {label}<small>{sub}</small>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {selectedDate && (
                    <div className="cp-field" ref={timeSlotsRef}>
                        <span><Clock size={13} style={{ verticalAlign: -2 }} /> Hora</span>
                        {loadingSlots ? (
                            <div className="cp-newappt-loading"><span className="cp-spin" /> Cargando horarios…</div>
                        ) : availableSlots.length === 0 ? (
                            <div className="cp-note"><AlertCircle size={16} /> No hay horarios disponibles para esta fecha.</div>
                        ) : (
                            <div className="cp-choice-grid">
                                {availableSlots.map((slot, idx) => (
                                    <button key={slot.start + idx} type="button" className={`cp-choice ${selectedTime === slot.start ? "is-active" : ""}`} onClick={() => chooseTime(slot.start)}>{slot.start}</button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {selectedTime && (
                    <>
                        <label className="cp-field">
                            <span>Nombre del cliente</span>
                            <input ref={nameRef} type="text" className="cp-input" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre" autoComplete="off" />
                        </label>
                        <div className="cp-field">
                            <CountryPhoneSelector
                                selectedCountry={selectedCountry}
                                onCountryChange={setSelectedCountry}
                                phoneValue={telefono}
                                onPhoneChange={setTelefono}
                                phoneError={phoneError}
                                setPhoneError={setPhoneError}
                                placeholder="Número de teléfono"
                                requeired={false}
                            />
                            <small>Opcional. Sin número, el cliente no recibe confirmación ni recordatorios ni puede mover su cita.</small>
                        </div>
                        <div className="cp-newappt-summary">
                            <div><b>{selectedService?.name}</b></div>
                            <div>{selectedDateFormatted} · {selectedTime}</div>
                            {selectedStaffId && staff.length > 1 && <div>Atiende {staff.find(s => s.id === selectedStaffId)?.name}</div>}
                        </div>
                    </>
                )}
            </div>
        </Sheet>
    );
}
